import { Badge } from "@/components/ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { formatUrlPath } from "@/lib/utils";
import { Page } from "@/schemas";
import { PenLine } from "lucide-react";

interface PageItemProps {
  page: Page;
  domainId: string;
  handleSelectPage: (domainId: string, pageId: string) => void;
}

const isDraft = (page: Page) => page.source === "wordpress" && !page.url;

export function PageItem({ page, domainId, handleSelectPage }: PageItemProps) {
  const draft = isDraft(page);
  const label = draft
    ? (page.wordpress?.wp_slug || "(untitled)")
    : (formatUrlPath(page.url) || "/");
  const showTitle = !!page.title && page.title !== label;
  const showKeyword = !!page.input_keyword;

  return (
    <HoverCard key={page.id}>
      <HoverCardTrigger asChild>
        <div
          className="flex cursor-pointer items-center gap-2 rounded-md border border-transparent px-2.5 py-2 text-sm transition-colors hover:border-border hover:bg-muted/60"
          onClick={() => handleSelectPage(domainId, page.id)}
        >
          {draft && (
            <PenLine className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate font-medium text-foreground">{label}</span>
            {showTitle && (
              <span className="truncate text-xs text-muted-foreground">
                {page.title}
              </span>
            )}
          </div>
          {showKeyword && (
            <Badge
              variant="secondary"
              className="ml-auto max-w-[10rem] flex-shrink-0 truncate"
            >
              {page.input_keyword}
            </Badge>
          )}
          {draft && (
            <span className="flex-shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              draft
            </span>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-auto p-2 text-sm">
        {draft
          ? `Draft — ${page.wordpress?.wp_slug || "unpublished"}`
          : page.url}
      </HoverCardContent>
    </HoverCard>
  );
}
